<template>
  <v-container id="partners-detail" fluid>
    <CountriesSelector
      :countries="countries"
      :loading="loading"
      :text="$t('partners.details.countries')"
      :title="partnerTitle"
      @change="selectCountry"
    />
    <v-card outlined :loading="loading" class="mt-2">
      <v-card-title class="font-weight-black">
        {{ $t('partners.details.settings.title') }}
      </v-card-title>
      <validation-observer ref="form" v-slot="{ invalid }">
        <v-form @submit.prevent="confirmSubmit" :disabled="loading">
          <v-card-text>
            <v-tabs v-model="tab.active" align-with-title :show-arrows="false">
              <v-tab class="ml-0" :class="{ 'error-tab': tab.errors[0] }">
                {{ $t('partners.details.settings.customization.label') }}
                <v-icon v-if="tab.errors[0]" color="error" small class="mr-1">mdi-alert-circle</v-icon>
              </v-tab>
              <v-tab-item eager class="mt-3">
                <v-card outlined :loading="loading">
                  <PartnerSettingsCustomization v-model="partner.settings[selectedCountry]" :loading="loading" />
                </v-card>
              </v-tab-item>
            </v-tabs>
          </v-card-text>
          <v-card-actions class="d-flex justify-center pb-4 pt-0">
            <v-btn color="primary" type="submit" :disabled="invalid" large depressed>
              {{ $t('subOrder.validate') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </validation-observer>
      <YesNoDialog
        v-model="modal.validate.display"
        :title="modal.validate.title"
        :text="modal.validate.text"
        @validate="onSubmit"
      />
    </v-card>
  </v-container>
</template>
<script lang="js">
  import { ValidationObserver } from 'vee-validate'
  import merge from 'lodash/merge';
  import CountriesSelector from '../../components/inputs/CountriesSelector.vue';
  import PartnerSettingsCustomization from '../../components/partners/PartnerSettingsCustomization.vue';
  import { EventBus } from '../../event-bus';
  import { PartnerService } from '../../services/partnerService';
  import { textExists } from '../../helpers/helper';
  import YesNoDialog from '../../components/dialogs/YesNoDialog.vue';
  import { defaultTheme } from '../../helpers/constante';
  import { mapState, mapMutations } from 'vuex';

  export default {
    name: "PartnerDetails",
    components: {
      CountriesSelector,
      ValidationObserver,
      PartnerSettingsCustomization,
      YesNoDialog
    },
    data() {
      return {
        partner: {
          name: null,
          settings: {
            default: structuredClone(defaultTheme)
          }
        },
        loading: false,
        tab: {
          active: 0,
          errors: [false]
        },
        selectedCountry: 'default',
        modal: {
          validate: {
            display: false,
            title: this.$t('partners.details.modal.validate.title'),
            text: this.$t('partners.details.modal.validate.text')
          }
        }
      }
    },
    created() {
      this.partnersService = new PartnerService(this.$auth)
    },
    mounted() {
      if (!textExists(this.partnerId)) {
        return this.redirectToList(this.$t('partners.details.missingId'))
      }
      this.get()
    },
    methods: {
      ...mapMutations({
        setPartnerTheme: 'setPartnerTheme',
      }),
      redirectToList(message = null) {
        let onComplete
        if (textExists(message)) {
          onComplete = () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: message,
              color: 'error',
            })
          }
        }
        return this.$router.replace(
          {
            name: 'partners',
          },
          onComplete
        )
      },
      displayOverlay(message) {
        this.$set(this, 'loading', true)
        EventBus.$emit('displayGlobalOverlay', { display: true, message })
      },
      hideOverlay() {
        this.$set(this, 'loading', false)
        EventBus.$emit('displayGlobalOverlay', { display: false })
      },
      async get() {
        if (this.loading) {
          return
        }
        this.displayOverlay(this.$t('partners.details.loadingText'))
        try {
          const partner = await this.partnersService.getPartnerById(this.partnerId)
          merge(this.partner, partner)
        } catch (e) {
          console.log(e)
        } finally {
          this.hideOverlay()
        }
      },
      selectCountry(country) {
        this.selectedCountry = country
      },
      async onSubmit() {
        this.displayOverlay(this.$t('partners.details.savingText'))
        try {
          const partner = await this.partnersService.updatePartner(this.partnerId, this.partner)
          merge(this.partner, partner)
          if (this.partner.tenant_id === this.currentGroup?.name) {
            this.setPartnerTheme(this.partner.settings)
          }
        } catch (e) {
          console.log(e)
        } finally {
          this.hideOverlay()
        }
      },
      async confirmSubmit() {
        const isValid = await this.$refs.form.validate()
        if (!isValid) return

        this.modal.validate.display = true
      }
    },
    computed: {
      ...mapState(['currentGroup']),
      partnerId() {
        return this.$route.params?.id?.trim()
      },
      partnerTitle() {
        if (!this.partner?.name) {
          return
        }
        const trad = this.$t("breadcrumbs.partners")
        return `${trad}: ${this.partner.name}`
      },
      countries() {
        return Object.keys(this.partner?.settings ?? {})
      }
    }
  }
</script>
